import { useState, useEffect } from 'react';

const CACHE_KEY = 'usernameCache';
const CACHE_VERSION = 1;
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days

interface CacheEntry {
  username: string;
  timestamp: number;
}

interface CacheData {
  version: number;
  entries: Record<string, CacheEntry>;
}

// In-memory mirror for fast access
const usernameCache = new Map<string, string>();

const API_URL = 'https://api.playcdu.co/uuid-to-name';

const loadCacheFromStorage = () => {
    try {
        const storedCache = localStorage.getItem(CACHE_KEY);
        if (storedCache) {
            const data: CacheData = JSON.parse(storedCache);
            if (data.version === CACHE_VERSION) {
                const now = Date.now();
                Object.entries(data.entries).forEach(([uuid, entry]) => {
                    if (now - entry.timestamp < CACHE_EXPIRY) {
                        usernameCache.set(uuid, entry.username);
                    }
                });
            }
        }
    } catch (error) {
        console.error("Failed to load username cache from localStorage", error);
    }
};

const saveCacheToStorage = () => {
    try {
        const entries: Record<string, CacheEntry> = {};
        usernameCache.forEach((username, uuid) => {
            entries[uuid] = { username, timestamp: Date.now() };
        });
        const data: CacheData = { version: CACHE_VERSION, entries };
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error("Failed to save username cache to localStorage", error);
    }
};

// Load initial cache
loadCacheFromStorage();

class UsernameCacheService {
  private pendingUuids = new Set<string>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private activeFetches = 0;

  private updateLoadingStatus() {
    const event = new CustomEvent('usernameCacheStatus', {
      detail: {
        loading: this.activeFetches > 0,
      },
    });
    document.dispatchEvent(event);
  }

  public async prefetchUsernames(uuids: string[]) {
    const uuidsToFetch = uuids.filter(uuid => uuid && !usernameCache.has(uuid));
    
    if (uuidsToFetch.length === 0) {
      return;
    }

    uuidsToFetch.forEach(uuid => this.pendingUuids.add(uuid));

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.fetchPendingUuids();
    }, 50); // Debounce for 50ms
  }

  /** Resolve any missing usernames before filtering or displaying search results. */
  public async ensureUsernames(uuids: string[]): Promise<void> {
    const missing = [...new Set(uuids.filter((u) => u && !usernameCache.has(u)))];
    if (missing.length === 0) return;
    await this.fetchChunks(missing);
  }

  private async fetchPendingUuids() {
    if (this.pendingUuids.size === 0) return;

    const uuidsToFetch = Array.from(this.pendingUuids);
    this.pendingUuids.clear();

    await this.fetchChunks(uuidsToFetch);
  }

  private async fetchChunks(uuidsToFetch: string[]) {
    if (uuidsToFetch.length === 0) return;

    this.activeFetches++;
    this.updateLoadingStatus();

    const CHUNK_SIZE = 50;
    const promises = [];

    for (let i = 0; i < uuidsToFetch.length; i += CHUNK_SIZE) {
      const chunk = uuidsToFetch.slice(i, i + CHUNK_SIZE);
      const promise = fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids: chunk }),
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.usernames && data.usernames.length === chunk.length) {
          chunk.forEach((uuid, index) => {
            usernameCache.set(uuid, data.usernames[index]);
          });
        } else {
          console.warn('Username fetch length mismatch, skipping chunk.', {
             requested: chunk.length, 
             got: data.usernames?.length || 0 
          });
        }
      })
      .catch(error => {
        console.error('Failed to fetch a chunk of usernames:', error);
        // We don't re-throw, so Promise.all doesn't fail for one bad chunk
      });
      promises.push(promise);
    }

    try {
      await Promise.all(promises);
      saveCacheToStorage();
    } finally {
      this.activeFetches--;
      this.updateLoadingStatus();
      document.dispatchEvent(new Event('usernamesUpdated'));
    }
  }

  public getName(uuid: string): string | null {
    return usernameCache.get(uuid) || null;
  }

  /**
   * Seeds a known UUID → username pair (e.g. from a Users API response that already
   * carried both) so components using `useUsername` skip the batch lookup.
   */
  public seed(uuid: string, username: string): void {
    if (!uuid || !username || usernameCache.get(uuid) === username) return;
    usernameCache.set(uuid, username);
    saveCacheToStorage();
    document.dispatchEvent(new Event('usernamesUpdated'));
  }
}

export const usernameCacheService = new UsernameCacheService();

export const useUsername = (uuid: string) => {
  const [username, setUsername] = useState(usernameCacheService.getName(uuid));

  useEffect(() => {
    // If the name is not in the cache, trigger a fetch for it.
    if (!username && uuid) {
      usernameCacheService.prefetchUsernames([uuid]);
    }

    const handleUpdate = () => {
      const name = usernameCacheService.getName(uuid);
      if (name) {
        setUsername(name);
      }
    };
    document.addEventListener('usernamesUpdated', handleUpdate);
    return () => document.removeEventListener('usernamesUpdated', handleUpdate);
  }, [uuid, username]);

  return username;
};

export const useUsernameCacheLoading = () => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleStatusUpdate = (event: CustomEvent) => {
      setLoading(event.detail.loading);
    };

    document.addEventListener('usernameCacheStatus', handleStatusUpdate as EventListener);
    
    return () => {
      document.removeEventListener('usernameCacheStatus', handleStatusUpdate as EventListener);
    };
  }, []);

  return loading;
};

/** Re-render when the global uuid→name cache is updated (e.g. after batch fetch). */
export const useUsernameCacheVersion = () => {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const handle = () => setVersion((v) => v + 1);
    document.addEventListener('usernamesUpdated', handle);
    return () => document.removeEventListener('usernamesUpdated', handle);
  }, []);
  return version;
};
